import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Tabs,
  Tab,
  Box,
  CircularProgress,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  Stack,
  Typography,
  Chip,
  Snackbar,
  Alert,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SyncIcon from '@mui/icons-material/Sync';
import { useManagers } from '../hooks/useManagers.ts';
import type { TenantDiagnostics } from '../hooks/useTenants.ts';
import InviteDialog from '../components/InviteDialog.tsx';
import {
  getTenant,
  getTenantStats,
  getTenantWelcome,
  getWaitlistStats,
  reconcileTenantBilling,
  resendTenantWelcome,
  simulateOccupancy,
} from '../services/apiService.ts';
import type { TenantWelcomeDelivery, TenantWelcomeStatus } from '../services/apiService.ts';
import TenantVerticalPackagingPanel from './TenantVerticalPackagingPanel.tsx';

const WELCOME_STATUSES: TenantWelcomeStatus[] = [
  'PENDING',
  'SENDING',
  'SENT',
  'FAILED',
  'UNDELIVERED',
  'ACTIVATED',
];

const knownWelcomeStatus = (value?: string | null): TenantWelcomeStatus | null =>
  WELCOME_STATUSES.includes(value as TenantWelcomeStatus)
    ? value as TenantWelcomeStatus
    : null;

const TenantDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const tenantId = id || '';
  const [tab, setTab] = useState(0);
  const [waitingCount, setWaitingCount] = useState<number | null>(null);
  const [tenantStats, setTenantStats] = useState<any | null>(null);
  const [tenantDetail, setTenantDetail] = useState<any | null>(null);
  const [tenantStatsLoading, setTenantStatsLoading] = useState(false);
  const [tenantDetailLoading, setTenantDetailLoading] = useState(false);
  const [welcomeDelivery, setWelcomeDelivery] = useState<TenantWelcomeDelivery | null>(null);
  const [welcomeLoading, setWelcomeLoading] = useState(false);
  const [welcomeLoadFailed, setWelcomeLoadFailed] = useState(false);
  const [welcomeResendOpen, setWelcomeResendOpen] = useState(false);
  const [welcomeResendLoading, setWelcomeResendLoading] = useState(false);
  const welcomeResendOperationRef = useRef<{ tenantId: string; generation: number } | null>(null);
  const welcomeRequestGenerationRef = useRef(0);
  const welcomeMountedRef = useRef(false);
  const welcomeTenantIdRef = useRef(tenantId);
  const [waitingCountLoading, setWaitingCountLoading] = useState(false);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    severity: 'success' | 'warning' | 'error';
    message: string;
  }>({
    open: false,
    severity: 'success',
    message: '',
  });
  const [simOpen, setSimOpen] = useState(false);
  const [freePct, setFreePct] = useState(60);
  const [heldPct, setHeldPct] = useState(10);
  const [durationMin, setDurationMin] = useState(5);
  const tenantDetailData = tenantDetail as TenantDiagnostics | null;

  const formatDateTime = (value?: string | null) => {
    if (!value) {
      return 'not recorded yet';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return new Intl.DateTimeFormat('ro-RO', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  };

  const formatCodeLabel = (value?: string | null, fallback = 'Not set') => {
    if (!value) {
      return fallback;
    }
    if (value === 'UNMAPPED') {
      return 'Needs catalog mapping';
    }
    return value
      .toLowerCase()
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  const loadTenantOverview = useCallback(async (activeRef?: { current: boolean }) => {
    if (!tenantId) {
      setTenantStats(null);
      setTenantDetail(null);
      return;
    }
    setTenantStatsLoading(true);
    setTenantDetailLoading(true);
    const [statsResult, detailResult] = await Promise.allSettled([
      getTenantStats(tenantId),
      getTenant(tenantId),
    ]);

    if (activeRef && !activeRef.current) {
      return;
    }

    if (statsResult.status === 'fulfilled') {
      setTenantStats(statsResult.value);
    } else {
      setTenantStats(null);
    }
    setTenantStatsLoading(false);

    if (detailResult.status === 'fulfilled') {
      setTenantDetail(detailResult.value);
    } else {
      setTenantDetail(null);
    }
    setTenantDetailLoading(false);
  }, [tenantId]);

  const loadWelcomeDelivery = useCallback(async (activeRef?: { current: boolean }) => {
    if (welcomeResendOperationRef.current?.tenantId === tenantId) {
      return;
    }
    const requestGeneration = ++welcomeRequestGenerationRef.current;
    const isCurrent = () => welcomeMountedRef.current
      && welcomeTenantIdRef.current === tenantId
      && (!activeRef || activeRef.current)
      && requestGeneration === welcomeRequestGenerationRef.current;
    if (!tenantId) {
      if (welcomeMountedRef.current) {
        setWelcomeDelivery(null);
        setWelcomeLoadFailed(false);
        setWelcomeLoading(false);
      }
      return;
    }

    setWelcomeLoading(true);
    setWelcomeLoadFailed(false);
    try {
      const result = await getTenantWelcome(tenantId);
      if (isCurrent()) {
        setWelcomeDelivery(result && typeof result === 'object' ? result : null);
      }
    } catch {
      if (isCurrent()) {
        setWelcomeDelivery(null);
        setWelcomeLoadFailed(true);
      }
    } finally {
      if (isCurrent()) {
        setWelcomeLoading(false);
      }
    }
  }, [tenantId]);

  useEffect(() => {
    welcomeMountedRef.current = true;
    return () => {
      welcomeMountedRef.current = false;
      welcomeRequestGenerationRef.current += 1;
      welcomeResendOperationRef.current = null;
    };
  }, []);

  useLayoutEffect(() => {
    welcomeTenantIdRef.current = tenantId;
    welcomeRequestGenerationRef.current += 1;
    welcomeResendOperationRef.current = null;
    setWelcomeDelivery(null);
    setWelcomeLoading(false);
    setWelcomeLoadFailed(false);
    setWelcomeResendOpen(false);
    setWelcomeResendLoading(false);
  }, [tenantId]);

  useEffect(() => {
    const activeRef = { current: true };
    void loadTenantOverview(activeRef);
    void loadWelcomeDelivery(activeRef);
    return () => {
      activeRef.current = false;
    };
  }, [loadTenantOverview, loadWelcomeDelivery]);

  useEffect(() => {
    const nextResendAt = welcomeDelivery?.nextResendAt;
    if (!nextResendAt) {
      return;
    }
    const remainingMs = Date.parse(nextResendAt) - Date.now();
    if (!Number.isFinite(remainingMs) || remainingMs <= 0) {
      return;
    }

    const activeRef = { current: true };
    const timeoutId = window.setTimeout(() => {
      void loadWelcomeDelivery(activeRef);
    }, Math.min(remainingMs + 50, 2_147_483_647));
    return () => {
      activeRef.current = false;
      window.clearTimeout(timeoutId);
    };
  }, [loadWelcomeDelivery, welcomeDelivery?.nextResendAt]);

  useEffect(() => {
    if (tab !== 2 || !tenantId) {
      return;
    }
    let active = true;
    setWaitingCountLoading(true);
    (async () => {
      try {
        const stats = await getWaitlistStats(tenantId);
        if (active) {
          setWaitingCount(stats?.currentWaiting ?? 0);
        }
      } catch {
        if (active) {
          setWaitingCount(null);
        }
      } finally {
        if (active) {
          setWaitingCountLoading(false);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [tab, tenantId]);

  const { rows: mgrs, loading: mgrLoading, error: mgrError, refetch } = useManagers(tenantId, tab === 1);
  const [inviteOpen, setInviteOpen] = useState(false);

  const activationTitle = tenantStatsLoading
    ? 'Loading activation state...'
    : tenantStats?.hasFirstBooking
      ? 'First booking reached'
      : 'Pending first booking';

  const activationSubtitle = tenantStatsLoading
    ? 'Checking activation markers...'
    : tenantStats?.timeToFirstBookingMinutes != null
      ? `Time to first booking: ${tenantStats.timeToFirstBookingMinutes} min`
      : 'Time to first booking: not reached yet';

  const commercialTitle = tenantDetailLoading
    ? 'Loading commercial state...'
    : tenantDetailData
      ? `${formatCodeLabel(tenantDetailData.catalogPlanCode, 'Manual')} / ${formatCodeLabel(tenantDetailData.billingState, 'Pilot')}`
      : 'Commercial state unavailable';

  const commercialSubtitle = tenantDetailLoading
    ? 'Fetching catalog plan and billing state...'
    : tenantDetailData
      ? `Active: ${tenantDetailData.commerciallyActive ? 'yes' : 'no'}`
      : 'Commercial diagnostics unavailable';

  const welcomeStatus = knownWelcomeStatus(welcomeDelivery?.status);
  const welcomeDeliveryFailed = welcomeStatus === 'FAILED' || welcomeStatus === 'UNDELIVERED';
  const welcomeFailureBlocker = welcomeDeliveryFailed
    ? `Owner welcome delivery status: ${welcomeStatus}${welcomeDelivery?.safeDetail ? ` - ${welcomeDelivery.safeDetail}` : ''}`
    : null;
  const readinessBlockers = Array.isArray(tenantDetailData?.paidPilotBlockers)
    ? tenantDetailData.paidPilotBlockers.filter((blocker: string) => blocker && blocker.trim().length > 0)
    : [];
  if (welcomeFailureBlocker && !readinessBlockers.includes(welcomeFailureBlocker)) {
    readinessBlockers.push(welcomeFailureBlocker);
  }
  const welcomeReadinessKnown = !welcomeLoading && !welcomeLoadFailed && welcomeStatus !== null;
  const paidPilotReady = tenantDetailData?.checkoutReady === true
    && welcomeReadinessKnown
    && readinessBlockers.length === 0;
  const readinessLabel = tenantDetailLoading || welcomeLoading
    ? 'Loading readiness...'
    : paidPilotReady
      ? 'Paid pilot ready'
      : tenantDetailData?.checkoutReady === false
        ? 'Checkout blocked'
        : readinessBlockers.length > 0
          ? 'Signoff blocked'
          : 'Readiness unknown';
  const readinessTone: 'success' | 'warning' | 'error' | 'default' = paidPilotReady
    ? 'success'
    : welcomeDeliveryFailed
      ? 'error'
      : tenantDetailData?.checkoutReady === false || readinessBlockers.length > 0
        ? 'warning'
        : 'default';
  const canReconcileBilling = !!tenantDetailData
    && (!!tenantDetailData.billingSubscriptionId || !!tenantDetailData.stripeCustomerId);

  const nextResendTimestamp = welcomeDelivery?.nextResendAt
    ? Date.parse(welcomeDelivery.nextResendAt)
    : Number.NaN;
  const welcomeCooldownActive = Number.isFinite(nextResendTimestamp)
    && nextResendTimestamp > Date.now();
  const welcomeStatusAllowsResend = welcomeStatus !== null
    && welcomeStatus !== 'SENDING'
    && welcomeStatus !== 'ACTIVATED';
  const canResendWelcome = welcomeDelivery?.resendAllowed === true
    && welcomeStatusAllowsResend
    && !welcomeCooldownActive
    && !welcomeLoading;
  const welcomeTone: 'success' | 'warning' | 'error' | 'info' | 'default' =
    welcomeStatus === 'ACTIVATED' || welcomeStatus === 'SENT'
      ? 'success'
      : welcomeStatus === 'FAILED' || welcomeStatus === 'UNDELIVERED'
        ? 'error'
        : welcomeStatus === 'SENDING'
          ? 'info'
          : welcomeStatus === 'PENDING'
            ? 'warning'
            : 'default';
  const welcomeHealth = welcomeDeliveryFailed
    ? 'Attention required'
    : welcomeStatus === 'ACTIVATED'
      ? 'Owner access activated'
      : welcomeStatus === 'SENT'
        ? 'Welcome sent; activation pending'
        : welcomeStatus === 'SENDING'
          ? 'Delivery in progress'
          : welcomeStatus === 'PENDING'
            ? 'Delivery pending'
            : 'Health unavailable';
  const welcomeResendAvailability = welcomeStatus === 'ACTIVATED'
    ? 'Resend is unavailable because owner access is already activated.'
    : welcomeStatus === 'SENDING'
      ? 'A welcome delivery is already in progress.'
      : welcomeCooldownActive
        ? `Resend available after ${formatDateTime(welcomeDelivery?.nextResendAt)}.`
        : welcomeDelivery && welcomeDelivery.resendAllowed !== true
          ? 'Resend is not currently allowed by the server.'
          : null;

  const paymentsTitle = tenantStatsLoading
    ? 'Loading payment stats...'
    : `${tenantStats?.paymentsPaid ?? 0} paid / ${tenantStats?.paymentsRequired ?? 0} required`;

  const paymentsSubtitle = tenantStatsLoading
    ? 'Fetching reservation payment posture...'
    : `Pending: ${tenantStats?.paymentsPending ?? 0} | Failed: ${tenantStats?.paymentsFailed ?? 0}`;

  const reservationsTitle = tenantStatsLoading
    ? 'Loading reservation stats...'
    : `${tenantStats?.reservations ?? 0} total`;

  const reservationsSubtitle = tenantStatsLoading
    ? 'Fetching reservation counters...'
    : `Confirmed: ${tenantStats?.reservationsConfirmed ?? 0} | Cancelled: ${tenantStats?.reservationsCancelled ?? 0} | No-show: ${tenantStats?.reservationsNoShow ?? 0}`;

  const waitlistLabel = waitingCountLoading
    ? 'Waitlist (...)'
    : waitingCount == null
      ? 'Waitlist'
      : `Waitlist (${waitingCount})`;

  const mgrCols: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'email', headerName: 'Email', flex: 1 },
    { field: 'firstName', headerName: 'First name', flex: 1 },
    { field: 'lastName', headerName: 'Last name', flex: 1 },
  ];

  const handleReconcileBilling = async () => {
    if (!tenantId) {
      return;
    }
    try {
      setReconcileLoading(true);
      const result = await reconcileTenantBilling(tenantId);
      await loadTenantOverview();
      setSnackbar({
        open: true,
        severity: result?.result === 'RECONCILED' ? 'success' : 'warning',
        message: `${result?.result || 'UNKNOWN'}: ${result?.reason || 'Billing reconcile finished.'}`,
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.message || 'Billing reconcile failed.',
      });
    } finally {
      setReconcileLoading(false);
    }
  };

  const handleResendWelcome = async () => {
    if (!tenantId || !canResendWelcome || welcomeResendOperationRef.current) {
      return;
    }

    const requestGeneration = ++welcomeRequestGenerationRef.current;
    const resendOperation = { tenantId, generation: requestGeneration };
    welcomeResendOperationRef.current = resendOperation;
    setWelcomeLoading(false);
    setWelcomeResendLoading(true);
    try {
      const result = await resendTenantWelcome(tenantId);
      if (welcomeMountedRef.current
        && welcomeTenantIdRef.current === tenantId
        && requestGeneration === welcomeRequestGenerationRef.current) {
        setWelcomeDelivery(result && typeof result === 'object' ? result : null);
        setWelcomeLoadFailed(false);
        setWelcomeResendOpen(false);
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'Welcome resend accepted. Delivery status has been refreshed.',
        });
      }
    } catch {
      if (welcomeMountedRef.current
        && welcomeTenantIdRef.current === tenantId
        && requestGeneration === welcomeRequestGenerationRef.current) {
        setSnackbar({
          open: true,
          severity: 'error',
          message: 'Welcome resend could not be requested. No delivery details were exposed.',
        });
      }
    } finally {
      if (welcomeResendOperationRef.current === resendOperation) {
        welcomeResendOperationRef.current = null;
        if (welcomeMountedRef.current && welcomeTenantIdRef.current === tenantId) {
          setWelcomeResendLoading(false);
        }
      }
    }
  };

  return (
    <Box p={2}>
      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
        <Tab label="Overview" />
        <Tab label="Managers" />
        <Tab iconPosition="end" icon={<NotificationsActiveIcon fontSize="small" />} label={waitlistLabel} />
        <Tab label="Vertical Packaging" />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<PhotoLibraryIcon />}
              onClick={() => navigate(`/dashboard/tenants/${tenantId}/media`)}
            >
              Manage Media (Menu & Gallery)
            </Button>
            <Button variant="outlined" onClick={() => setSimOpen(true)}>Simulate occupancy</Button>
          </Box>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <Box sx={{ p: 2, border: '1px solid rgba(15,23,42,0.12)', borderRadius: 2, minWidth: 220 }}>
              <Typography variant="overline" color="text.secondary">Activation</Typography>
              <Typography variant="h6">{activationTitle}</Typography>
              <Typography variant="body2" color="text.secondary">{activationSubtitle}</Typography>
            </Box>
            <Box sx={{ p: 2, border: '1px solid rgba(15,23,42,0.12)', borderRadius: 2, minWidth: 220 }}>
              <Typography variant="overline" color="text.secondary">Commercial</Typography>
              <Typography variant="h6">{commercialTitle}</Typography>
              <Typography variant="body2" color="text.secondary">{commercialSubtitle}</Typography>
            </Box>
            <Box sx={{ p: 2, border: '1px solid rgba(15,23,42,0.12)', borderRadius: 2, minWidth: 220 }}>
              <Typography variant="overline" color="text.secondary">Payments</Typography>
              <Typography variant="h6">{paymentsTitle}</Typography>
              <Typography variant="body2" color="text.secondary">{paymentsSubtitle}</Typography>
            </Box>
            <Box sx={{ p: 2, border: '1px solid rgba(15,23,42,0.12)', borderRadius: 2, minWidth: 220 }}>
              <Typography variant="overline" color="text.secondary">Reservations</Typography>
              <Typography variant="h6">{reservationsTitle}</Typography>
              <Typography variant="body2" color="text.secondary">{reservationsSubtitle}</Typography>
            </Box>
          </Stack>
          <Box
            data-testid="welcome-delivery-card"
            sx={{ p: 2, border: '1px solid rgba(15,23,42,0.12)', borderRadius: 2, mb: 2 }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', md: 'center' }}
              spacing={1}
              sx={{ mb: 1.5 }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2">Owner access / Welcome delivery</Typography>
                <Chip
                  label={welcomeLoading && !welcomeDelivery ? 'LOADING' : welcomeStatus || 'UNAVAILABLE'}
                  color={welcomeTone}
                  size="small"
                  variant={welcomeStatus ? 'filled' : 'outlined'}
                />
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => { void loadWelcomeDelivery(); }}
                  disabled={welcomeLoading || welcomeResendLoading}
                >
                  Refresh status
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setWelcomeResendOpen(true)}
                  disabled={!canResendWelcome || welcomeResendLoading}
                >
                  {welcomeResendLoading ? 'Resending...' : 'Resend welcome'}
                </Button>
              </Stack>
            </Stack>

            {welcomeLoading && !welcomeDelivery && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">Loading welcome delivery status...</Typography>
              </Stack>
            )}

            {welcomeLoadFailed && (
              <Alert severity="warning">
                Welcome delivery status is unavailable. Refresh to try again.
              </Alert>
            )}

            {!welcomeLoading && !welcomeLoadFailed && !welcomeDelivery && (
              <Typography variant="body2" color="text.secondary">
                No welcome delivery state was returned.
              </Typography>
            )}

            {welcomeDelivery && (
              <>
                <Typography
                  variant="body2"
                  color={welcomeDeliveryFailed ? 'error.main' : 'text.secondary'}
                  sx={{ mb: 1.5 }}
                >
                  Health: {welcomeHealth}
                  {welcomeDelivery.safeDetail ? ` - ${welcomeDelivery.safeDetail}` : ''}
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Owner</Typography>
                    <Typography variant="body2">{welcomeDelivery.ownerEmailMasked || 'not reported'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Attempts</Typography>
                    <Typography variant="body2">{welcomeDelivery.attempts ?? 'not reported'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Created</Typography>
                    <Typography variant="body2">{formatDateTime(welcomeDelivery.createdAt)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Last attempt</Typography>
                    <Typography variant="body2">{formatDateTime(welcomeDelivery.lastAttemptAt)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Sent</Typography>
                    <Typography variant="body2">{formatDateTime(welcomeDelivery.sentAt)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Activated</Typography>
                    <Typography variant="body2">{formatDateTime(welcomeDelivery.activatedAt)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Next resend</Typography>
                    <Typography variant="body2">{formatDateTime(welcomeDelivery.nextResendAt)}</Typography>
                  </Box>
                </Stack>
                {welcomeResendAvailability && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    {welcomeResendAvailability}
                  </Typography>
                )}
              </>
            )}
          </Box>
          {tenantDetailData && (
            <Box sx={{ p: 2, border: '1px solid rgba(15,23,42,0.12)', borderRadius: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Commercial diagnostics</Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Catalog plan</Typography>
                  <Typography variant="body2">{formatCodeLabel(tenantDetailData.catalogPlanCode, 'Manual')}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Plan</Typography>
                  <Typography variant="body2">{formatCodeLabel(tenantDetailData.plan, 'Not set')}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Catalog source</Typography>
                  <Typography variant="body2">{tenantDetailData.catalogPlanSource || 'manual / none'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Stripe customer</Typography>
                  <Typography variant="body2">{tenantDetailData.stripeCustomerId || 'not created yet'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Subscription</Typography>
                  <Typography variant="body2">{tenantDetailData.billingSubscriptionId || 'not active yet'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Price id</Typography>
                  <Typography variant="body2">{tenantDetailData.billingPriceId || 'missing'}</Typography>
                </Box>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                {(tenantDetailData.catalogSuggestedModules || []).map((module: string) => (
                  <Chip key={`suggested-${module}`} label={`Catalog: ${module}`} size="small" color="primary" variant="outlined" />
                ))}
                {(tenantDetailData.enabledModules || []).map((module: string) => (
                  <Chip key={`enabled-${module}`} label={`Enabled: ${module}`} size="small" />
                ))}
              </Stack>
              {Array.isArray(tenantDetailData.commercialWarnings) && tenantDetailData.commercialWarnings.length > 0 && (
                <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                  {tenantDetailData.commercialWarnings.map((warning: string) => (
                    <Typography key={warning} variant="body2" color="warning.main">
                      {warning}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Box>
          )}
          {tenantDetailData && (
            <Box sx={{ p: 2, border: '1px solid rgba(15,23,42,0.12)', borderRadius: 2, mb: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle2">Paid pilot readiness</Typography>
                  <Chip
                    label={readinessLabel}
                    color={readinessTone}
                    size="small"
                    variant={paidPilotReady ? 'filled' : 'outlined'}
                  />
                </Stack>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<SyncIcon />}
                  onClick={handleReconcileBilling}
                  disabled={!canReconcileBilling || reconcileLoading}
                >
                  {reconcileLoading ? 'Reconciling...' : 'Reconcile Billing'}
                </Button>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                This section is the operator truth for checkout launch and paid-pilot signoff.
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Checkout ready</Typography>
                  <Typography variant="body2">{tenantDetailData.checkoutReady ? 'yes' : 'no'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Billing last synced</Typography>
                  <Typography variant="body2">{formatDateTime(tenantDetailData.billingUpdatedAt)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Last webhook</Typography>
                  <Typography variant="body2">{tenantDetailData.lastBillingWebhookType || 'none'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Webhook at</Typography>
                  <Typography variant="body2">{formatDateTime(tenantDetailData.lastBillingWebhookAt)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Webhook outcome</Typography>
                  <Typography variant="body2">{tenantDetailData.lastBillingWebhookOutcome || 'not recorded yet'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Last checkout session</Typography>
                  <Typography variant="body2">{tenantDetailData.lastBillingCheckoutSessionId || 'not started yet'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Checkout status</Typography>
                  <Typography variant="body2">{tenantDetailData.lastBillingCheckoutStatus || 'not started yet'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Checkout opened</Typography>
                  <Typography variant="body2">{formatDateTime(tenantDetailData.lastBillingCheckoutStartedAt)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Checkout completed</Typography>
                  <Typography variant="body2">{formatDateTime(tenantDetailData.lastBillingCheckoutCompletedAt)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Checkout expires</Typography>
                  <Typography variant="body2">{formatDateTime(tenantDetailData.lastBillingCheckoutExpiresAt)}</Typography>
                </Box>
              </Stack>
              <Stack spacing={0.75} sx={{ mt: 2 }}>
                {readinessBlockers.length > 0 ? readinessBlockers.map((blocker: string) => (
                  <Typography
                    key={blocker}
                    variant="body2"
                    color={blocker === welcomeFailureBlocker ? 'error.main' : 'warning.main'}
                  >
                    {blocker}
                  </Typography>
                )) : (
                  <Typography variant="body2" color="text.secondary">
                    No paid pilot blockers reported.
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
            {[
              ['Manager', tenantStatsLoading ? null : tenantStats?.managerActivated],
              ['Location', tenantStatsLoading ? null : tenantStats?.hasLocation],
              ['Space', tenantStatsLoading ? null : tenantStats?.hasSpace],
              ['Table', tenantStatsLoading ? null : tenantStats?.hasTable],
              ['Open hours', tenantStatsLoading ? null : tenantStats?.hasOpenHours],
              ['First booking', tenantStatsLoading ? null : tenantStats?.hasFirstBooking],
            ].map(([label, done]) => (
              <Box
                key={String(label)}
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 999,
                  border: '1px solid',
                  borderColor: done == null
                    ? 'rgba(15, 23, 42, 0.12)'
                    : done
                      ? 'rgba(46, 125, 50, 0.24)'
                      : 'rgba(237, 108, 2, 0.24)',
                  bgcolor: done == null
                    ? 'rgba(15, 23, 42, 0.04)'
                    : done
                      ? 'rgba(46, 125, 50, 0.08)'
                      : 'rgba(237, 108, 2, 0.08)',
                }}
              >
                <Typography variant="caption" color={done == null ? 'text.secondary' : done ? 'success.main' : 'warning.main'}>
                  {done == null ? 'Loading' : done ? 'Closed' : 'Missing'} | {label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {tab === 1 && (
        <Box>
          <IconButton color="primary" onClick={() => setInviteOpen(true)}><PersonAddIcon /></IconButton>
          {mgrLoading ? <CircularProgress /> : mgrError ? (
            <Typography variant="body2" color="warning.main">
              Managers unavailable right now. The overview stays usable, but the managers list could not be loaded.
            </Typography>
          ) : (
            <DataGrid rows={mgrs} columns={mgrCols} autoHeight />
          )}
        </Box>
      )}

      {tab === 2 && (
        <Box>
          <Button
            variant="contained"
            startIcon={<NotificationsActiveIcon />}
            onClick={() => navigate(`/dashboard/tenants/${tenantId}/waitlist`)}
          >
            Open Waitlist
          </Button>
        </Box>
      )}

      {tab === 3 && (
        <TenantVerticalPackagingPanel tenantId={tenantId} />
      )}

      {inviteOpen && (
        <InviteDialog
          tenantId={Number(tenantId)}
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          onSuccess={() => { refetch(); }}
        />
      )}

      <Dialog
        open={welcomeResendOpen}
        onClose={() => {
          if (!welcomeResendLoading) {
            setWelcomeResendOpen(false);
          }
        }}
      >
        <DialogTitle>Resend owner welcome?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Request a new activation welcome for {welcomeDelivery?.ownerEmailMasked || 'the tenant owner'}?
            The request uses the existing SUPER_ADMIN session and does not expose credentials.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWelcomeResendOpen(false)} disabled={welcomeResendLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => { void handleResendWelcome(); }}
            disabled={!canResendWelcome || welcomeResendLoading}
          >
            {welcomeResendLoading ? 'Resending...' : 'Confirm resend'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={simOpen} onClose={() => setSimOpen(false)}>
        <DialogTitle>Simulate occupancy</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1, minWidth: 320 }}>
            <Box>
              <Typography variant="body2">Free %</Typography>
              <Slider value={freePct} onChange={(_, value) => setFreePct(value as number)} step={5} min={0} max={100} valueLabelDisplay="auto" />
            </Box>
            <Box>
              <Typography variant="body2">Held %</Typography>
              <Slider value={heldPct} onChange={(_, value) => setHeldPct(value as number)} step={5} min={0} max={50} valueLabelDisplay="auto" />
            </Box>
            <Box>
              <Typography variant="body2">Duration (minutes)</Typography>
              <Slider value={durationMin} onChange={(_, value) => setDurationMin(value as number)} step={5} min={5} max={60} valueLabelDisplay="auto" />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSimOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                await simulateOccupancy(tenantId, { freePct, heldPct, durationMin });
              } finally {
                setSimOpen(false);
              }
            }}
          >
            Start
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TenantDetailPage;
